import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Landing }      from '@pages/Landing';
import { Login }        from '@pages/Login';
import { MyAccount }    from '@pages/MyAccount';
import { Users }        from '@pages/admin/Users';
import { Stations }     from '@pages/admin/Stations';
import { ProtectedRoute, Unauthorized } from './ProtectedRoute';
import { ROLES }        from '@config/platforms';

// Lazy-loaded platform shells (replace with real platform implementations)
import { lazy, Suspense } from 'react';
const PlatformShell = lazy(() => import('@pages/platforms/PlatformShell'));

const PlatformLoader = ({ platformId }) => (
  <Suspense fallback={<div className="min-h-screen bg-gl-void" />}>
    <PlatformShell platformId={platformId} />
  </Suspense>
);

const PLATFORM_ROUTES = [
  {
    path:       'hr',
    platformId: 'hr',
    roles:      [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.HR_MANAGER, ROLES.HR_STAFF, ROLES.SUPERVISOR, ROLES.STAFF],
  },
  {
    path:       'safety',
    platformId: 'safety',
    roles:      [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.SAFETY_OFFICER, ROLES.SUPERVISOR, ROLES.STAFF],
  },
  {
    path:       'training',
    platformId: 'training',
    roles:      [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.TRAINER, ROLES.SUPERVISOR, ROLES.STAFF],
  },
  {
    path:       'maintenance',
    platformId: 'maintenance',
    roles:      [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MAINTENANCE_MANAGER, ROLES.MAINTENANCE_LEAD, ROLES.MAINTENANCE, ROLES.SUPERVISOR],
  },
  {
    path:       'logistics',
    platformId: 'logistics',
    roles:      [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.LOGISTICS_LEAD, ROLES.LOGISTICS, ROLES.PLANNER, ROLES.SUPERVISOR],
  },
  {
    path:       'dashboard',
    platformId: 'dashboard',
    roles:      [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PLANNER, ROLES.SUPERVISOR, ROLES.HR_MANAGER, ROLES.LOGISTICS_LEAD],
  },
];

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login"   element={<Login />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Landing hub — deliberately gated, not public. It lists every platform's
            name/description, which is internal-only information; anonymous
            visitors get sent straight to /login instead of seeing that list. */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Landing />
            </ProtectedRoute>
          }
        />

        {/* Self-service — any authenticated user, no platform/role restriction */}
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <MyAccount />
            </ProtectedRoute>
          }
        />

        {/* Admin — user provisioning, super_admin/admin only */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
              <Users />
            </ProtectedRoute>
          }
        />

        {/* Admin — the station registry every platform reads */}
        <Route
          path="/admin/stations"
          element={
            <ProtectedRoute roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
              <Stations />
            </ProtectedRoute>
          }
        />

        {/* Protected platform routes */}
        {PLATFORM_ROUTES.map(({ path, platformId, roles }) => (
          <Route
            key={path}
            path={`/platforms/${path}/*`}
            element={
              <ProtectedRoute roles={roles} platformId={platformId}>
                <PlatformLoader platformId={platformId} />
              </ProtectedRoute>
            }
          />
        ))}

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
